<template>
  <v-container>
    <app-loading :loading="loading">
      <div>
        <app-header :buttonAction="action" backButton :backAction="backAction">
          <template v-slot:title>{{ data.audio_text.title }}</template>
          <template v-slot:subtitle>{{ data.audio_text.subtitle }}</template>
        </app-header>
        <app-explanation v-intersect="onIntersect" class="mt-5">
          <template v-slot:body>{{ data.audio_text.description }}</template>
        </app-explanation>
        <app-button-player
          class="mt-7 mb-10"
          asPlayer
          :floating="!intersecting"
          :src="data.audio_text && data.audio_text.audio_file ? data.audio_text.audio_file : ''"
        />
        <div v-show="!intersecting" style="height: 200px" />
        <template v-if="data.comprehension">
          <div
            v-for="(question, index) in data.comprehension.question_group.questions"
            :key="index"
          >
            <app-comment :id="question.uuid" class="mt-7" :body="JSON.parse(question.content)">
              <template v-slot:title>{{ question.title }}</template>
              <template #extra-buttons>
                <v-btn
                  icon
                  class="ml-auto"
                  color="#29284c"
                  style="position: absolute; right: 4px; bottom: 4px"
                  @click="onCopy($event, question.uuid)"
                >
                  <v-icon> mdi-content-copy </v-icon>
                </v-btn>
              </template>
            </app-comment>
            <app-comment class="mt-7" :src="student.avatar">
              <template v-slot:title>
                <div class="d-flex">
                  Answer
                  <v-btn icon @click="onPaste">
                    <v-icon color="#29284c"> mdi-content-paste </v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:body>
                <app-body-answer
                  v-model="answer.content"
                  :finished="finished"
                  :readonly="finished"
                  :teacher="JSON.parse(question.good_answer)"
                />
                <v-row justify="center" class="mt-6">
                  <v-btn
                    v-if="!finished"
                    small
                    depressed
                    width="100"
                    :loading="loadingFinish"
                    class="white--text text-capitalize"
                    color="#29284c"
                    @click="onFinish"
                    >Finish</v-btn
                  >
                  <ProgressResetButton
                    v-else
                    :stepType="direction === 'teach' ? 'TEACH_LISTENING' : 'PRACTICE_LISTENING'"
                    :stepUuid="source.audio_text_step"
                    @reset="onReset"
                  />
                </v-row>
              </template>
            </app-comment>
          </div>
        </template>
        <app-comment-group
          v-if="data.comprehension"
          ref="commentGroup"
          :uuid="data.comprehension.comment_group"
        />
      </div>
    </app-loading>
    <app-student-note v-if="!isTeacher && qualification.note" :note="qualification.note" />
    <app-qualification
      v-if="isTeacher && !qualification.note && qualification.uuid && direction === 'practice'"
      :qualification="qualification"
      @new-qual="qualification = $event"
    />
    <app-qualification-dialog
      v-model="dialog"
      :qualification="qualification"
      @new-qual="qualification = $event"
      @input="onInput"
    />
  </v-container>
</template>
<script>
import Header from "@/components/Lessons/Lesson/components/Header.vue";
import Explanation from "@/components/Lessons/Lesson/components/Explanation";
import Comment from "@/components/Lessons/Lesson/components/Comment";
import BodyAnswer from "@/components/Lessons/Lesson/components/BodyAnswer";
import { getAudioText, getAnswer, postAnswer } from "@/api/api";
import directionMixin from "@/mixins/directionMixin";
import studentMixin from "@/mixins/studentMixin";
import Loading from "@/components/Loading.vue";
import Qualification from "@/components/Utils/Qualification.vue";
import QualificationDialog from "@/components/Utils/QualificationDialog.vue";
import StudentNote from "@/components/Utils/StudentNote.vue";
import CommentGroup from "@/components/Lessons/Lesson/components/CommentGroup.vue";
import ButtonPlayer from "../ButtonPlayer.vue";
import ProgressResetButton from "@/components/Utils/ProgressResetButton.vue";

export default {
  components: {
    appHeader: Header,
    appExplanation: Explanation,
    appComment: Comment,
    appBodyAnswer: BodyAnswer,
    appLoading: Loading,
    appQualification: Qualification,
    appQualificationDialog: QualificationDialog,
    appStudentNote: StudentNote,
    appCommentGroup: CommentGroup,
    appButtonPlayer: ButtonPlayer,
    ProgressResetButton,
  },
  mixins: [directionMixin, studentMixin],
  data() {
    return {
      data: {},
      loading: true,
      answer: {
        content: "",
      },
      qualification: {},
      dialog: false,
      finished: false,
      loadingFinish: false,
      intersecting: true,
    };
  },
  methods: {
    onReset() {
      this.answer.content = "";
      this.answer.uuid = undefined;
      this.finished = false;
      this.$refs.commentGroup?.updateComments(this.data.comprehension.comment_group);
    },
    onIntersect(entries) {
      this.intersecting = entries[0].isIntersecting;
    },
    async action() {
      if (this.finished && !this.qualification.uuid) {
        this.qualification = await this.qualify();
      }
      this.updateProgress("audio_text_step");
      this.$emit("next-step");
    },
    backAction() {
      this.$emit("prev-step");
    },
    onInput(e) {
      if (!e) {
        this.$emit("next-step");
      }
    },
    async onFinish() {
      this.loadingFinish = true;
      if (!this.finished && !this.answer.uuid) {
        const response = await postAnswer(this.answer);
        if (response) {
          this.answer = response.data;
        }
      }
      this.finished = true;
      this.loadingFinish = false;
    },
    async onPaste() {
      try {
        const text = await navigator.clipboard.readText();
        this.answer.content = text;
      } catch (error) {
        console.error(error);
      }
    },
    async onCopy(e, id) {
      const element = document.querySelector("#" + id).querySelector(".ProseMirror");

      // eslint-disable-next-line no-undef
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([element.innerHTML], { type: "text/html" }),
        "text/plain": new Blob([element.innerText], { type: "text/plain" }),
      });
      navigator.clipboard.write([clipboardItem]);
    },
  },
  async mounted() {
    this.loading = true;
    let response = await getAudioText(this.source.audio_text_step, this.direction);
    this.data = response.data;

    response = await getAnswer({
      student__email: this.student.email,
      question__uuid: this.data.comprehension.question_group.questions[0].uuid,
    });
    if (response.data.count) {
      [this.answer] = response.data.results;
      this.finished = true;
    } else {
      this.answer.student = this.student.email;
      this.answer.question = this.data.comprehension.question_group.questions[0].uuid;
    }

    this.qualification = await this.fetchQualification(
      this.student.email,
      this.data.comprehension.question_group.uuid,
    );

    this.loading = false;
  },
};
</script>
